import {
  useCreateRecordBase,
  useFindRecordBase,
  usePopulateBase,
  useQueryBase,
} from '@unite-us/json-api-resources';
import { useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import Notifier from 'common/helpers/Notifier';
import { DEFAULT_API, getAdapter } from '../api/config';
import {
  useCurrentEmployee,
  useProvider,
} from '../context/InteractionsContext';

const defaultOnError = () => Notifier.dispatch('error', 'Something went wrong...');

const buildHttpConfig = ({ employeeId, providerId } = {}) => ({
  headers: {
    ...(employeeId && { 'X-EMPLOYEE-ID': employeeId }),
    ...(providerId && { 'X-PROVIDER-ID': providerId }),
  },
});

function useHttpConfig() {
  const currentEmployee = useCurrentEmployee();
  const provider = useProvider();

  return useMemo(
    () => buildHttpConfig({
      employeeId: currentEmployee?.id,
      providerId: provider?.id,
    }),
    [currentEmployee?.id, provider?.id],
  );
}

export const useFind = (modelName, query, params = {}) => {
  const httpConfig = useHttpConfig();
  const { api = DEFAULT_API, queryConfig = {}, ...jsonApiParams } = params;

  return useQueryBase(
    modelName,
    query,
    {
      httpConfig,
      ...jsonApiParams,
      api: getAdapter(api),
      useQuery,
      queryConfig: { enabled: true, ...queryConfig },
    },
  );
};

export const useFindRecord = (modelName, id, params = {}) => {
  const httpConfig = useHttpConfig();
  const { api = DEFAULT_API, queryConfig = {}, ...jsonApiParams } = params;

  return useFindRecordBase(
    modelName,
    id,
    {
      httpConfig,
      ...jsonApiParams,
      api: getAdapter(api),
      useQuery,
      queryConfig: { enabled: true, ...queryConfig },
    },
  );
};

export const useCreateRecord = (modelName, options = {}) => {
  const httpConfig = useHttpConfig();
  const callback = useCreateRecordBase({
    useMutation,
    mutationConfig: {
      ...options.mutationConfig,
      onError: options.mutationConfig?.onError ?? defaultOnError,
    },
  });

  const { mutateAsync } = callback;
  callback.mutate = null;
  callback.mutateAsync = null;
  // JSONAPIAdapter.createRecord from json-api-resources does not pass httpConfig on POST,
  // so instead we're merging it into the request here.
  callback.createRecord = (data, overrideOptions = {}) => {
    const baseApi = getAdapter(options.api ?? DEFAULT_API);
    const api = {
      createRecord: baseApi.createRecord.bind({
        serializer: baseApi.serializer,
        http: {
          ...baseApi.http,
          post: (url, payload, config) => baseApi.http.post(url, payload, {
            ...httpConfig,
            ...(config ?? {}),
            headers: { ...httpConfig?.headers, ...(config?.headers ?? {}) },
          }),
        },
      }),
    };
    return mutateAsync(
      {
        model: modelName,
        data,
        params: {
          httpConfig,
          api,
        },
      },
      overrideOptions.mutationConfig,
    );
  };

  return callback;
};

export const usePopulate = (relationshipName, modelName, data, params = {}) => {
  const httpConfig = useHttpConfig();
  const { api = DEFAULT_API, queryConfig = {}, ...jsonApiParams } = params;

  return usePopulateBase(
    relationshipName,
    modelName,
    data,
    {
      httpConfig,
      ...jsonApiParams,
      api: getAdapter(api),
      useQuery,
      queryConfig: { enabled: true, ...queryConfig },
    },
  );
};
